import { Dispatch, SetStateAction, useCallback, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { NotificationController } from 'core-components/library/popup'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { IPartnerWithFlagRequests } from 'partner360/models/partnerFlagRequests/IPartnerWithFlagRequests'
import { TFlagChangeRequestStatus } from 'partner360/models/partnerFlagRequests/TFlagChangeRequestStatus'
import { addTopPartnerRequest } from 'partner360/services/partnersWithFlagRequests/addTopPartnerRequest'
import { changeRequestStatus } from 'partner360/services/partnersWithFlagRequests/changeRequestStatus'

interface IUseFlagRequestActionsParams {
  fYear?: number
  approvers: string[]
  setPartners: Dispatch<SetStateAction<IPartnerWithFlagRequests[]>>
}

export const useFlagRequestActions = ({
  fYear = 0,
  approvers,
  setPartners,
}: IUseFlagRequestActionsParams) => {
  const [isLoading, setIsLoading] = useState(false)
  const pendingRequest = useRef(false)
  const user = useSelector(selectUser)

  const isApprover = useMemo(() => approvers.includes(user.email), [approvers, user.email])

  const createRequest = useCallback(
    async (partnerId: string, request: Record<string, 0 | 1>) => {
      if (pendingRequest.current) return null
      pendingRequest.current = true
      setIsLoading(true)
      try {
        const res = await addTopPartnerRequest({ fYear, partnerId, request })

        setPartners((partners) =>
          partners.map((partner) =>
            partner.PartnerId === res.PartnerId
              ? {
                  ...partner,
                  Request: !isApprover ? res.Request : null,
                  RequestCreated: res.RequestCreated,
                  RequestCreatedBy: res.RequestCreatedBy,
                  RequestStatus: res.RequestStatus,
                  BitClass: res.BitClass,
                  RequestId: res.RequestId,
                }
              : partner
          )
        )

        return res
      } catch {
        NotificationController.showError({
          message: 'Failed to create the request. Please try again.',
        })
        return null
      } finally {
        pendingRequest.current = false
        setIsLoading(false)
      }
    },
    [fYear, isApprover, setPartners]
  )

  const updateRequestStatus = useCallback(
    async (requestId: number, status: TFlagChangeRequestStatus) => {
      if (pendingRequest.current) return null
      pendingRequest.current = true
      setIsLoading(true)
      try {
        const res = await changeRequestStatus(requestId, status)

        setPartners((partners) =>
          partners.map((partner) =>
            partner.PartnerId === res.PartnerId
              ? {
                  ...partner,
                  RequestCreated: res.RequestCreated,
                  RequestCreatedBy: res.RequestCreatedBy,
                  RequestStatus: res.RequestStatus,
                  BitClass: res.BitClass,
                  RequestId: res.RequestId,
                }
              : partner
          )
        )

        return res
      } catch {
        NotificationController.showError({
          message:
            status === 'Deleted'
              ? 'Failed to remove the request. Please try again.'
              : 'Failed to update the request status. Please try again.',
        })
        return null
      } finally {
        pendingRequest.current = false
        setIsLoading(false)
      }
    },
    [setPartners]
  )

  return { isLoading, isApprover, createRequest, updateRequestStatus }
}
